(function () {
  const STORAGE_KEY = 'flow_delivery_location';
  const GEOIP_URL = 'https://ipapi.co/json/';

  if (window.FlowDeliveryLocation && typeof window.FlowDeliveryLocation.init === 'function') {
    window.FlowDeliveryLocation.init(document);
    return;
  }

  const initializedScopes = new WeakSet();
  let widgets = [];
  let activeWidget = null;
  let geoipRequested = false;
  let modalIdCounter = 0;
  let previousBodyOverflow = null;

  function getSaved() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function save(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage unavailable
    }
  }

  function render(data) {
    const label = formatLocation(data);
    if (!label) return;

    setLocationText(label);
  }

  function formatLocation(data) {
    if (!data) return '';

    if (data.zip && data.region) return data.zip + ', ' + data.region;
    if (data.zip && data.country) return data.zip + ', ' + data.country;

    const parts = [];
    if (data.region) parts.push(data.region);
    if (data.country) parts.push(data.country);

    return parts.join(', ');
  }

  function setLocationText(value) {
    pruneWidgets();

    widgets.forEach(function (widget) {
      widget.locationText.textContent = value;
    });
  }

  function openModal(widget) {
    if (!widget.modal || !widget.zipInput) return;

    if (activeWidget === widget && widget.modal.classList.contains('is-open')) return;

    if (activeWidget && activeWidget !== widget) {
      closeModal(activeWidget, { restoreFocus: false });
    }

    activeWidget = widget;
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    widget.modal.classList.add('is-open');
    widget.modal.setAttribute('aria-hidden', 'false');

    if (widget.trigger) {
      widget.trigger.setAttribute('aria-expanded', 'true');
    }

    const saved = getSaved();
    if (saved && saved.zip) {
      widget.zipInput.value = saved.zip;
    }

    window.setTimeout(function () {
      widget.zipInput.focus();
    }, 100);
  }

  function closeModal(widget, options) {
    const targetWidget = widget || activeWidget;
    if (!targetWidget || !targetWidget.modal) return;

    targetWidget.modal.classList.remove('is-open');
    targetWidget.modal.setAttribute('aria-hidden', 'true');

    if (targetWidget.trigger) {
      targetWidget.trigger.setAttribute('aria-expanded', 'false');
    }

    document.body.style.overflow = previousBodyOverflow || '';
    previousBodyOverflow = null;

    if (activeWidget === targetWidget) {
      activeWidget = null;
    }

    if (!options || options.restoreFocus !== false) {
      focusTrigger(targetWidget);
    }
  }

  function focusTrigger(widget) {
    if (!widget.trigger || !widget.trigger.isConnected) return;

    window.setTimeout(function () {
      widget.trigger.focus();
    }, 0);
  }

  function confirmLocation(widget) {
    if (!widget.zipInput) return;

    const zip = widget.zipInput.value.trim();
    if (!zip) return;

    const saved = getSaved() || {};
    const country = saved.country || 'United States';
    const nextLocation = Object.assign({}, saved, {
      country: country,
      zip: zip,
    });

    save(nextLocation);
    setLocationText(zip + ', ' + country);
    closeModal(widget);

    fetch('https://api.zippopotam.us/us/' + encodeURIComponent(zip))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (data && data.places && data.places.length) {
          nextLocation.region = data.places[0].state;
          save(nextLocation);
          setLocationText(zip + ', ' + nextLocation.region);
        }
      })
      .catch(function () {
        // Keep fallback display
      });
  }

  function discoverScopes(container) {
    const root = container || document;
    const scopes = [];

    if (root.matches && root.matches('[data-delivery-location-scope]')) {
      scopes.push(root);
    }

    root.querySelectorAll('[data-delivery-location-scope]').forEach(function (scope) {
      scopes.push(scope);
    });

    if (scopes.length) return scopes;

    const legacyWidget = buildLegacyWidget();
    return legacyWidget ? [legacyWidget.root] : [];
  }

  function buildWidget(scope) {
    const locationText = scope.querySelector('[data-delivery-location-text]');
    const trigger = scope.querySelector('[data-delivery-location-trigger]');
    const modal = scope.querySelector('[data-delivery-location-modal]');

    if (!locationText || !trigger || !modal) return null;

    return {
      root: scope,
      locationText: locationText,
      trigger: trigger,
      modal: modal,
      zipInput: modal.querySelector('[data-delivery-zip-input]'),
      confirmBtn: modal.querySelector('[data-delivery-confirm]'),
      title: modal.querySelector('.delivery-location-modal__title'),
    };
  }

  function buildLegacyWidget() {
    const locationText = document.getElementById('delivery-location-text');
    const trigger = document.getElementById('delivery-location-trigger');
    const modal = document.getElementById('delivery-location-modal');

    if (!locationText || !trigger || !modal) return null;

    return {
      root: modal,
      locationText: locationText,
      trigger: trigger,
      modal: modal,
      zipInput: document.getElementById('delivery-zip-input'),
      confirmBtn: document.getElementById('delivery-confirm-btn'),
      title: modal.querySelector('.delivery-location-modal__title'),
    };
  }

  function setupWidget(widget) {
    if (!widget || !widget.zipInput || !widget.confirmBtn || initializedScopes.has(widget.root)) return;

    initializedScopes.add(widget.root);
    widgets.push(widget);
    setupAria(widget);

    widget.trigger.addEventListener('click', function () {
      openModal(widget);
    });

    widget.confirmBtn.addEventListener('click', function () {
      confirmLocation(widget);
    });

    widget.modal.querySelectorAll('[data-close-modal]').forEach(function (element) {
      element.addEventListener('click', function () {
        closeModal(widget);
      });
    });

    widget.zipInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        confirmLocation(widget);
      }
    });
  }

  function setupAria(widget) {
    if (!widget.modal.id) {
      modalIdCounter += 1;
      widget.modal.id = 'delivery-location-modal-' + modalIdCounter;
    }

    if (widget.title && !widget.title.id) {
      widget.title.id = widget.modal.id + '-title';
    }

    widget.trigger.setAttribute('aria-haspopup', 'dialog');
    widget.trigger.setAttribute('aria-expanded', 'false');
    widget.trigger.setAttribute('aria-controls', widget.modal.id);

    widget.modal.setAttribute('role', 'dialog');
    widget.modal.setAttribute('aria-modal', 'true');
    widget.modal.setAttribute('aria-hidden', 'true');

    if (widget.title) {
      widget.modal.setAttribute('aria-labelledby', widget.title.id);
    }
  }

  function hydrateLocation() {
    const saved = getSaved();
    if (saved) {
      render(saved);
      if (formatLocation(saved)) return;
    }

    if (geoipRequested) return;
    geoipRequested = true;

    fetch(GEOIP_URL)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (data && data.region && data.country_name) {
          const loc = {
            region: data.region,
            country: data.country_name,
            zip: data.postal || '',
          };
          save(loc);
          render(loc);
        }
      })
      .catch(function () {
        // Silently fail. Default text stays as "United States".
      });
  }

  function pruneWidgets() {
    widgets = widgets.filter(function (widget) {
      return widget.root.isConnected && widget.modal.isConnected;
    });

    if (activeWidget && (!activeWidget.root.isConnected || !activeWidget.modal.isConnected)) {
      closeModal(activeWidget, { restoreFocus: false });
    }
  }

  function init(container) {
    const scopes = discoverScopes(container || document);
    scopes.forEach(function (scope) {
      setupWidget(buildWidget(scope) || buildLegacyWidget());
    });

    pruneWidgets();

    if (widgets.length) {
      hydrateLocation();
    }
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && activeWidget && activeWidget.modal.classList.contains('is-open')) {
      closeModal(activeWidget);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (activeWidget && event.target.contains(activeWidget.root)) {
      closeModal(activeWidget, { restoreFocus: false });
    }
    pruneWidgets();
  });

  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    render(getSaved());
  });

  window.FlowDeliveryLocation = {
    init: init,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
